// Component: ModeToggle.jsx
// Purpose: Live/Paper/All mode segmented control for Algo Trading hub

import React, { useRef, useEffect, useState, useCallback } from 'react';

// Mode colors reuse the DS badge tokens (theme-swapped): Live = profit (green),
// Paper = info (blue), All = neutral. Keeps the top filter in lockstep with the pills.
const modes = [
  { key: 'live', label: 'Live', color: 'var(--badge-profit-dot)' },
  { key: 'paper', label: 'Paper', color: 'var(--badge-info-dot)' },
  { key: 'all', label: 'All', color: 'var(--badge-neut-dot)' },
];

function ModeToggle({ mode, onChange }) {
  // --> Refs for each button to measure position/size for the sliding pill
  const containerRef = useRef(null);
  const buttonRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });

  // --> Compute the active mode's color for the pill background
  const activeMode = modes.find((m) => m.key === mode) || modes[0];

  // --> Measure the active button and position the sliding pill
  const updatePillPosition = useCallback(() => {
    const activeButton = buttonRefs.current[mode];
    const container = containerRef.current;
    if (!activeButton || !container) return;

    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();

    setPillStyle({
      left: buttonRect.left - containerRect.left,
      width: buttonRect.width,
    });
  }, [mode]);

  useEffect(() => {
    updatePillPosition();
    //! Recalculate on window resize to keep pill aligned
    window.addEventListener('resize', updatePillPosition);
    return () => window.removeEventListener('resize', updatePillPosition);
  }, [updatePillPosition]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center rounded-md bg-bg-2 p-[3px] border border-[var(--divider)]"
    >
      {/* --> Sliding pill indicator */}
      <div
        className="absolute top-[3px] bottom-[3px] rounded-[5px] border transition-all duration-200 ease-out"
        style={{
          left: `${pillStyle.left}px`,
          width: `${pillStyle.width}px`,
          // color-mix keeps these valid for rgb() inputs — the old `${color}15`
          // concat produced an invalid color, so the border fell back to
          // currentColor (a loud white line in dark mode).
          backgroundColor: `color-mix(in srgb, ${activeMode.color} 14%, transparent)`,
          borderColor: `color-mix(in srgb, ${activeMode.color} 30%, transparent)`,
        }}
      />

      {/* --> Mode buttons */}
      {modes.map((m) => {
        const isActive = mode === m.key;
        return (
          <button
            key={m.key}
            ref={(el) => {
              buttonRefs.current[m.key] = el;
            }}
            onClick={() => onChange(m.key)}
            className={`
              relative z-10 flex items-center gap-1.5 px-3 py-[5px] rounded-[5px] text-[11.5px] font-semibold
              transition-colors duration-200 cursor-pointer select-none
              ${isActive
                ? 'text-white'
                : 'text-fg-3 hover:text-fg-1'
              }
            `}
            style={isActive ? { color: m.color } : undefined}
          >
            {/* --> Status dot: filled for live, outline for paper, double ring for all */}
            {m.key === 'live' && (
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{
                  backgroundColor: isActive ? m.color : 'currentColor',
                }}
              />
            )}
            {m.key === 'paper' && (
              <span
                className="inline-block w-2 h-2 rounded-full border-[1.5px]"
                style={{
                  borderColor: isActive ? m.color : 'currentColor',
                  backgroundColor: 'transparent',
                }}
              />
            )}
            {m.key === 'all' && (
              <span className="relative inline-flex items-center justify-center w-2.5 h-2.5">
                <span
                  className="absolute inset-0 rounded-full border-[1.5px]"
                  style={{ borderColor: isActive ? m.color : 'currentColor' }}
                />
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: isActive ? m.color : 'currentColor' }}
                />
              </span>
            )}
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

export default ModeToggle;
